import React from "react";
import { Player, PostStatsResponse, PostDetails } from "@/services/bedrock";
import { PlayerHeader, NetworkTabs, MediaPanel, CaptionPanel, PostStatusPanel } from "./";
import { getUrlForSource } from "@/containers/CalendarPage/components/EventModal/utils";
import { AppContext } from "@/providers";
import { sourceTypeLabels } from "@/containers/Coach/Compliance/options";
import { Badge } from "@/components/ui";

interface PostSummaryProps {
  post: PostDetails;
  player: Player;
  withWritePermission: boolean;
  activeNetworkTab: string;
  setActiveNetworkTab: (val: string) => void;
  postStats?: PostStatsResponse;
  pollingSocialPosts: Record<number, string | null>;
  onStartPolling: (socialPostId: number, postedTimestamp: string | null) => void;
  handleReload: () => void;
}

export function PostSummary({
  post,
  player,
  withWritePermission,
  activeNetworkTab,
  setActiveNetworkTab,
  postStats,
  pollingSocialPosts,
  onStartPolling,
  handleReload,
}: PostSummaryProps) {
  const { userContext } = React.useContext(AppContext);
  const selectedSocialNetwork = post.postSocialNetworks.find(sn => sn.socialNetworkId === activeNetworkTab);

  const networkBreakdown = postStats?.networkBreakdown.find(nb => nb.socialNetworkId === activeNetworkTab);
  const networkStats = networkBreakdown?.latestStats;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-row gap-2 justify-between items-center">
        <PlayerHeader player={player} />
        {post.sourceType && post.sourceType !== "POST" && post.sourceId && (
          <a href={getUrlForSource(userContext, post.sourceType, post.sourceId)} target="_blank">
            <Badge variant={"outline"}>Source: {sourceTypeLabels[post.sourceType]}</Badge>
          </a>
        )}
      </div>

      <div className="w-full">
        <NetworkTabs post={post} activeNetworkTab={activeNetworkTab} setActiveNetworkTab={setActiveNetworkTab} />

        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_1fr] min-h-[400px] border border-slate-100 rounded-2xl overflow-hidden shadow-sm bg-white">
          <MediaPanel post={post} socialPost={selectedSocialNetwork} />

          <CaptionPanel
            caption={selectedSocialNetwork?.postedCaption || post.message}
            isFinalCaption={!!selectedSocialNetwork?.postedCaption}
          />

          <PostStatusPanel
            key={selectedSocialNetwork?.id}
            post={post}
            socialPost={selectedSocialNetwork}
            withWritePermission={withWritePermission}
            isPolling={selectedSocialNetwork ? selectedSocialNetwork.id in pollingSocialPosts : false}
            onStartPolling={onStartPolling}
            networkStats={networkStats}
            handleReload={handleReload}
          />
        </div>
      </div>
    </div>
  );
}
